import { useState, useEffect } from 'react';
import apiClient from '../services/api';

export interface ConversationMessage {
  speaker: 'system' | 'candidate' | 'interviewer';
  message: string;
  timestamp?: string;
  type?: string;
  event?: string;
  reason?: string;
}

export interface ConversationLogData {
  conversationLog: ConversationMessage[];
  hasLog: boolean;
  loading: boolean;
  error: string | null;
}

export const useConversationLog = (interviewId: string | null) => {
  const [data, setData] = useState<ConversationLogData>({
    conversationLog: [],
    hasLog: false,
    loading: false,
    error: null,
  });

  const fetchConversationLog = async (id: string) => {
    try {
      setData((prev) => ({ ...prev, loading: true, error: null }));

      const response = await apiClient.get(`/interviews/${id}/result`);

      if (response.data.success && response.data.data) {
        let conversationLog: ConversationMessage[] = [];

        // 会話ログのデータ構造を正規化
        if (Array.isArray(response.data.data.conversationLog)) {
          conversationLog = response.data.data.conversationLog;
        } else if (response.data.data.conversationLog?.messages) {
          conversationLog = response.data.data.conversationLog.messages;
        } else if (
          response.data.data.conversationLog &&
          typeof response.data.data.conversationLog === 'object'
        ) {
          // 数字キー形式 ("0", "1", "2"...) を配列に変換
          const logEntries = Object.entries(response.data.data.conversationLog)
            .filter(([key, value]) => /^\d+$/.test(key) && value && typeof value === 'object')
            .sort(([a], [b]) => parseInt(a) - parseInt(b))
            .map(([, value]) => value as ConversationMessage);
          conversationLog = logEntries;
        }

        setData({
          conversationLog,
          hasLog: conversationLog.length > 0,
          loading: false,
          error: null,
        });
      } else {
        setData((prev) => ({
          ...prev,
          loading: false,
          error: '会話ログの取得に失敗しました',
        }));
      }
    } catch (error) {
      console.error('会話ログ取得エラー:', error);
      setData((prev) => ({
        ...prev,
        loading: false,
        error: 'ネットワークエラーが発生しました',
      }));
    }
  };

  useEffect(() => {
    if (interviewId) {
      fetchConversationLog(interviewId);
    } else {
      setData({
        conversationLog: [],
        hasLog: false,
        loading: false,
        error: null,
      });
    }
  }, [interviewId]);

  return {
    ...data,
    refetch: interviewId ? () => fetchConversationLog(interviewId) : () => {},
  };
};
